import { useState, useCallback } from 'react';
import CropperClass from 'react-easy-crop';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Cropper = CropperClass as unknown as React.FC<any>;
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Slider, Box, Typography,
} from '@mui/material';

interface Area {
  x: number; y: number; width: number; height: number;
}

async function getCroppedBlob(imageSrc: string, pixelCrop: Area, mimeType: string): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = imageSrc;
  });

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, pixelCrop.width, pixelCrop.height,
  );

  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Canvas is empty')), mimeType)
  );
}

interface ImageCropDialogProps {
  open: boolean;
  imageSrc: string;
  fileName: string;
  mimeType: string;
  onConfirm: (blob: Blob, fileName: string) => void;
  onClose: () => void;
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open, imageSrc, fileName, mimeType, onConfirm, onClose,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, mimeType);
    onConfirm(blob, fileName);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crop Photo</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%', height: 320, bgcolor: '#000', borderRadius: 1, overflow: 'hidden' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </Box>
        <Box sx={{ mt: 2, px: 1 }}>
          <Typography variant="caption" color="text.secondary">Zoom</Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={(_, v) => setZoom(v as number)}
            size="small"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleConfirm} variant="contained">Apply</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageCropDialog;
